import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/pro-solid-svg-icons';
import Select from 'react-select';
import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import emptyFunction from '@avtechsoftware/ra-javascript-utilities/src/utilities/EmptyFunction';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import FormAlertBox
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormAlertBox/FormAlertBox';
import ModalComponent from '../../../layout/Modals/ModalComponent/ModalComponent';
import AddressType from '../AddressType';
import Address from '../../../../classes/Address';
import statesList from '../../../../utility/statesList';
import provincesList from '../../../../utility/provincesList';

function EditAddressModal({
  closeModal,
  addressType,
  address,
  submitAddress,
  countries,
  isShippingRateCalculated,
  shippingOptions,
  handleRefreshShippingOptions,
  shippingFormErrors,
  shippingRatesPending,
}) {
  const addressObj = new Address(
    address.companyName,
    address.contactName,
    address.attention,
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.zipCode,
    address.country,
    address.phoneNumber,
    address.id,
  );
  const [selectedAddress, setAddress] = useState(addressObj);
  const [addressChanged, setAddressChanged] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState({});
  const [canSubmit, setCanSubmit] = useState(false);

  const refreshShippingOptions = (addr) => {
    handleRefreshShippingOptions(addr);
    setCanSubmit(false);
    setAddressChanged(false);
  };

  const setSelectedAddress = (addr) => {
    setAddress(addr);
    setAddressChanged(true);
    if (addressType === AddressType.BILLING) {
      setCanSubmit(true);
    }
  };

  const handleCloseModal = () => closeModal();

  const handleOnChangeCountry = (e) => {
    const { value } = e.target;
    const selectedValue = Number(value);
    const indexOfCountry = countries.findIndex((c) => c.crmId === selectedValue);
    const country = countries[indexOfCountry];
    setSelectedAddress({
      ...selectedAddress,
      country: { id: selectedValue, name: country.name, code: country.abbreviation },
      city: '',
      state: '',
    });
  };

  const renderShippingButton = () => {
    if (shippingRatesPending) {
      return (
        <button
          type="button"
          className="btn btn-link"
          disabled
          onClick={() => {}}
        >
          <span>
            <ProgressSpinner useSmallSpinner progressText="Refresh Shipping Options" />
          </span>
        </button>
      );
    }

    return (
      <button
        type="button"
        className="btn btn-link"
        onClick={() => refreshShippingOptions(selectedAddress)}
      >
        <span>
          <FontAwesomeIcon icon={faRefresh} />
          {' '}
          Refresh Shipping Options
        </span>
      </button>
    );
  };

  /* istanbul ignore next */
  return (
    <ModalComponent
      size="lg"
      show
      close={handleCloseModal}
      showConfirmCallToAction
      isBtnDisabled={!canSubmit}
      confirmButtonAction={() => {
        setAddressChanged(false);
        submitAddress(addressType, selectedAddress, selectedShipping);
        handleCloseModal();
      }}
      confirmButtonText="Submit"
    >
      <Modal.Header
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        closeButton
      >
        <Modal.Title className="ml-auto">
          {addressType === AddressType.SHIPPING ? 'Shipping Address' : 'Billing Address'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form>
          <FormErrorHandler errorList={shippingFormErrors} fieldList={['zip', 'line1', 'country', 'city']}>
            <FormAlertBox />
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="col-form-label col-sm-3 text-sm-right text-nowrap"><b>Contact Name</b></label>
              <div className="col-sm-8">
                <AltErrorWrapper fieldId="contactName">
                  <input
                    type="text"
                    value={selectedAddress.contactName}
                    onChange={(e) => setSelectedAddress({
                      ...selectedAddress,
                      contactName: e.target.value,
                    })}
                    className="form-control always-inline"
                  />
                </AltErrorWrapper>
              </div>
            </div>
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="col-form-label col-sm-3 text-sm-right text-nowrap"><b>Company Name</b></label>
              <div className="col-sm-8">
                <AltErrorWrapper fieldId="companyName">
                  <input
                    type="text"
                    value={selectedAddress.companyName}
                    onChange={(e) => setSelectedAddress({
                      ...selectedAddress,
                      companyName: e.target.value,
                    })}
                    className="form-control always-inline"
                  />
                </AltErrorWrapper>
              </div>
            </div>
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="col-form-label col-sm-3 text-sm-right text-nowrap">
                <b>
                  Street
                  Address
                </b>
              </label>
              <div className="col-sm-8">
                <AltErrorWrapper fieldId="line1">
                  <input
                    type="text"
                    value={selectedAddress.line1}
                    onChange={(e) => setSelectedAddress({
                      ...selectedAddress,
                      line1: e.target.value,
                    })}
                    className="form-control always-inline"
                  />
                </AltErrorWrapper>
              </div>
            </div>
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="col-form-label col-sm-3 text-sm-right text-nowrap">{' '}</label>
              <div className="col-sm-8">
                <input
                  value={selectedAddress.line2}
                  onChange={(e) => setSelectedAddress({
                    ...selectedAddress,
                    line2: e.target.value,
                  })}
                  className="form-control always-inline"
                />
              </div>
            </div>
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                className="col-form-label col-sm-3 text-sm-right text-nowrap"
              >
                <b>Country</b>
              </label>
              <div className="col-sm-8">
                <AltErrorWrapper fieldId="country">

                  <select
                    name="country"
                    className="form-control always-inline"
                    value={selectedAddress.country && Number(selectedAddress.country.id)}
                    onChange={handleOnChangeCountry}
                  >
                    <option value={0}>- Choose a Country -</option>
                    {countries.filter((c) => c.crmId !== 0)
                      .map((country, index) => (
                        <option
                          key={`${country.crmId}-${index}`}
                          value={country.crmId}
                        >
                          {country.name}
                        </option>
                      ))}
                  </select>
                </AltErrorWrapper>
              </div>
            </div>
            {
              (selectedAddress.country && selectedAddress.country.id === 1)
              && (
                <div className="form-group row">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label
                    className="col-form-label col-sm-3 text-sm-right text-nowrap"
                  >
                    <b>State</b>
                  </label>
                  <div className="col-sm-8">
                    <select
                      name="state"
                      className="form-control always-inline"
                      value={selectedAddress.state}
                      onChange={(e) => {
                        setSelectedAddress({
                          ...selectedAddress,
                          state: e.target.value,
                          city: '',
                        });
                      }}
                    >
                      <option value="">- Choose a state -</option>
                      {statesList.map((state, index) => (
                        <option key={`${state.abbreviation}-${index}`} value={state.abbreviation}>
                          {state.abbreviation}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )
          }
            {
              selectedAddress.country && selectedAddress.country.id === 4
            && (
              <div className="form-group row">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  className="col-form-label col-sm-3 text-sm-right text-nowrap"
                >
                  <b>Province</b>
                </label>
                <div className="col-sm-8">
                  <select
                    name="state"
                    className="form-control always-inline"
                    value={selectedAddress.state}
                    onChange={(e) => {
                      setSelectedAddress({
                        ...selectedAddress,
                        state: e.target.value,
                        city: '',
                      });
                    }}
                  >
                    <option value="">Choose a Province</option>
                    {provincesList.map((state, index) => (
                      <option key={`${state.abbreviation}-${index}`} value={state.abbreviation}>
                        {state.abbreviation}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )
          }
            {
            (selectedAddress.country
              && selectedAddress.country.id !== 4 && selectedAddress.country.id !== 1) && (
              <div className="form-group row">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  className="col-form-label col-sm-3 text-sm-right text-nowrap"
                >
                  <b>State/Province</b>
                </label>
                <div className="col-sm-8">
                  <input
                    name="state"
                    type="text"
                    className="form-control always-inline"
                    value={selectedAddress.state}
                    onChange={(e) => {
                      setSelectedAddress({
                        ...selectedAddress,
                        state: e.target.value,
                        city: '',
                      });
                    }}
                  />
                </div>
              </div>
            )
          }
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="col-form-label col-sm-3 text-sm-right text-nowrap"><b>City</b></label>
              <div className="col-sm-8">
                <AltErrorWrapper fieldId="city">
                  <input
                    type="text"
                    value={selectedAddress.city}
                    onChange={(e) => {
                      setSelectedAddress({
                        ...selectedAddress,
                        city: e.target.value,
                      });
                    }}
                    className="form-control always-inline"
                  />
                </AltErrorWrapper>
              </div>
            </div>
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="col-form-label col-sm-3 text-sm-right text-nowrap">
                <b>
                  Zip/Postal
                  Code
                </b>
              </label>
              <div className="col-sm-8">
                <AltErrorWrapper fieldId="zip">
                  <input
                    type="text"
                    value={selectedAddress.zipCode}
                    onChange={(e) => {
                      setSelectedAddress({
                        ...selectedAddress,
                        zipCode: e.target.value,
                      });
                    }}
                    className="form-control always-inline"
                  />
                </AltErrorWrapper>
              </div>
            </div>
            {addressType === AddressType.SHIPPING && (
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="col-form-label col-sm-3 text-sm-right text-nowrap">
                <b>
                  Ship Method
                </b>
              </label>
              <div className="col-sm-8">
                {
                  (!isShippingRateCalculated || addressChanged)
                    ? renderShippingButton()
                    : (
                      <Select
                        options={[
                          ...shippingOptions
                            .map((item) => ({
                              ...item,
                              value: item.serviceCode,
                            })),
                        ]}
                        placeholder="Select a shipping option.."
                        onChange={(selected) => {
                          setSelectedShipping(selected);
                          setCanSubmit(true);
                        }}
                        isOptionDisabled={(option) => option.isDisabled}
                        formatOptionLabel={(data) => (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                          >
                            <span>{data.name}</span>
                            <span>{data.displayPrice}</span>
                          </div>
                        )}
                      />
                    )
}
              </div>
            </div>
            )}
          </FormErrorHandler>
        </form>
      </Modal.Body>
    </ModalComponent>
  );
}

EditAddressModal.defaultProps = {
  countries: [],
  isShippingRateCalculated: false,
  shippingOptions: [],
  handleRefreshShippingOptions: emptyFunction,
  shippingFormErrors: [],
  shippingRatesPending: false,
};

EditAddressModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  addressType: PropTypes.number.isRequired,
  address: PropTypes.instanceOf(Address).isRequired,
  submitAddress: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number || null,
    crmId: PropTypes.number,
    code: PropTypes.number || null,
    name: PropTypes.string,
    abbreviation: PropTypes.string || null,
  })),
  isShippingRateCalculated: PropTypes.bool,
  shippingOptions: PropTypes.arrayOf(
    PropTypes.shape({ serviceCode: PropTypes.string }),
  ),
  handleRefreshShippingOptions: PropTypes.func,
  shippingFormErrors: PropTypes.arrayOf(PropTypes.string),
  shippingRatesPending: PropTypes.bool,
};

export default EditAddressModal;
